import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { ConfirmationService } from '../../../services/confirmation.service';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { TemplateService } from '../../../car-wash-website/services/template.service';
import {
  TabItem,
  TabHeaderComponent,
} from '../../../shared/components/tab-header/tab-header.component';
import { environment } from '../../../../environments/environment';
import { LoggerService } from '../../../services/logger.service';

type DomainType = 'subdomain' | 'customdomain';

export interface WebsiteSettings {
  siteTitle: string;
  siteDescription: string;
  siteUrl: string;
  isPublished: boolean;
}

@Component({
  selector: 'app-website-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, TabHeaderComponent],
  templateUrl: './website-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./website-settings.component.scss'],
})
export class WebsiteSettingsComponent implements OnInit, OnChanges, OnDestroy {
  private readonly logger = inject(LoggerService);
  @Input() websiteData: any;
  @Input() websiteId: string | null = null;
  @Input() websitePages: any[] = []; // Pages from API
  @Input() initialActiveSidebarItem: 'general' | 'domains' | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<WebsiteSettings>();
  @Output() unpublish = new EventEmitter<void>();
  @Output() domainConfirmed = new EventEmitter<void>();

  activeTab: 'site-settings' | 'site-images' | 'analytics' = 'site-settings';
  activeSidebarItem: 'general' | 'domains' | null = 'general';
  activeSeoItem: string | null = null; // Changed to string to support dynamic page IDs

  // Tabs configuration for tab-header component
  tabs: TabItem[] = [
    { id: 'site-settings', label: 'Site Settings' },
    { id: 'site-images', label: 'Site Images' },
    { id: 'analytics', label: 'Analytics' },
  ];

  domainType: DomainType = 'subdomain';
  subdomainName = '';
  customDomainName = '';
  isDomainSaving = false;
  private domainSavingType: DomainType | null = null;

  // Extract domain from environment baseUrl
  // Example: 'https://carwash.way.com' -> 'carwash.way.com' -> '.carwash.way.com'
  /**
   * Return a leading-dot subdomain suffix derived from environment.baseUrl; removes protocol, path and port, with fallback '.carwashdev.way.com'.
   * @example
   * subdomainSuffix()
   * @returns {{string}} Return a leading-dot domain suffix or the fallback '.carwashdev.way.com'.
   **/
  get subdomainSuffix(): string {
    try {
      const baseUrl = environment.baseUrl || '';
      if (!baseUrl) {
        return '.carwashdev.way.com'; // Fallback
      }

      let domain = baseUrl.replace(/^https?:\/\//, '');
      const [domainNoPath] = domain.split('/');
      const [domainNoPort] = domainNoPath.split(':');
      domain = domainNoPort;

      // Return with leading dot
      return domain ? `.${domain}` : '.carwashdev.way.com';
    } catch (error) {
      this.logger.error('Error extracting domain from baseUrl:', error);
      return '.carwashdev.way.com'; // Fallback
    }
  }

  // Validation errors
  subdomainError = '';
  customDomainError = '';

  // Site Images
  faviconPreview: string | null = null;
  socialPreview: string | null = null;

  // Analytics Settings
  analyticsSettings = {
    googleAnalyticsId: '',
    cookieBannerId: '',
  };

  // SEO Settings - Store settings for each page dynamically
  seoSettings: Record<string, { pageTitle: string; pageDescription: string }> = {};

  settings: WebsiteSettings = {
    siteTitle: '',
    siteDescription: '',
    siteUrl: 'yoursite.url',
    isPublished: true,
  };

  // Debounced save for SEO settings
  private seoSaveSubject = new Subject<{
    pageId: string;
    pageTitle: string;
    pageDescription: string;
  }>();
  isSavingSeo = false; // Public for template access
  private destroy$ = new Subject<void>();

  constructor(
    private readonly messageService: NotificationService,
    private readonly confirmationService: ConfirmationService,
    private readonly templateService: TemplateService
  ) {}

  /**
   * Initialize component state, load website data and pages, and set up debounced SEO save subscription.
   * @example
   * gOnInit()
   * // returns nothing (void)
   * @returns {void} Performs initialization and side-effects (sets settings, previews, analytics, loads pages, and subscribes to SEO save events).
   **/
  ngOnInit(): void {
    // Set initial active sidebar item if provided
    if (this.initialActiveSidebarItem) {
      this.activeSidebarItem = this.initialActiveSidebarItem;
    }

    if (this.websiteData) {
      this.settings = {
        siteTitle: this.websiteData.siteTitle || '',
        siteDescription: this.websiteData.siteDescription || '',
        siteUrl: this.websiteData.siteUrl || 'yoursite.url',
        isPublished:
          this.websiteData.isPublished !== undefined ? this.websiteData.isPublished : true,
      };

      // Load favicon from branding if available
      if (this.websiteData.branding?.logo?.url) {
        this.faviconPreview = this.websiteData.branding.logo.url;
      } else if (this.websiteData.logoUrl) {
        this.faviconPreview = this.websiteData.logoUrl;
      }

      // Load social preview if available, otherwise use default from Figma
      if (this.websiteData.socialPreview) {
        this.socialPreview = this.websiteData.socialPreview;
      } else {
        // Use the downloaded social preview image as default example
        this.socialPreview = 'assets/images/site-images/social-preview.png';
      }

      // Load analytics settings if available
      if (this.websiteData.analytics) {
        this.analyticsSettings = {
          googleAnalyticsId: this.websiteData.analytics.googleAnalyticsId || '',
          cookieBannerId: this.websiteData.analytics.cookieBannerId || '',
        };
      }
    } else {
      // Set default social preview image when no website data
      this.socialPreview = 'assets/images/site-images/social-preview.png';
    }

    // Load website pages from API data (will also be called in ngOnChanges if input changes)
    this.loadWebsitePages();

    // Setup debounced save for SEO settings (800ms debounce - professional delay)
    this.seoSaveSubject
      .pipe(
        debounceTime(800), // Wait 800ms after user stops typing
        distinctUntilChanged((prev, curr) => {
          // Only skip if all values are the same
          return (
            prev.pageId === curr.pageId &&
            prev.pageTitle === curr.pageTitle &&
            prev.pageDescription === curr.pageDescription
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(data => {
        if (data.pageId && this.websiteId) {
          this.savePageSeoSettings(data.pageId, data.pageTitle, data.pageDescription);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.seoSaveSubject.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload pages when websitePages input changes (including first change)
    if (changes['websitePages']) {
      this.loadWebsitePages();
    }

    // Also reload if websiteData changes
    if (changes['websiteData']) {
      this.loadWebsitePages();
    }
  }

  /**
   * Load website pages from API data and initialize SEO settings
   */
  loadWebsitePages(): void {
    // Use @Input websitePages if provided, otherwise try to get from websiteData
    let pagesToUse: any[] = [];

    if (this.websitePages && this.websitePages.length > 0) {
      // Already provided via @Input
      pagesToUse = [...this.websitePages]; // Create a copy to avoid reference issues
    } else if (this.websiteData?.website_pages && Array.isArray(this.websiteData.website_pages)) {
      // Check if websiteData has website_pages array (from API response structure)
      pagesToUse = this.websiteData.website_pages;
    } else if (
      this.websiteData?.data?.website_pages &&
      Array.isArray(this.websiteData.data.website_pages)
    ) {
      // Handle case where data is nested under 'data' property
      pagesToUse = this.websiteData.data.website_pages;
    }

    // Update websitePages if we found pages from websiteData
    if (pagesToUse.length > 0 && (!this.websitePages || this.websitePages.length === 0)) {
      this.websitePages = pagesToUse;
    }

    // Initialize SEO settings from API data
    const pages =
      this.websitePages && this.websitePages.length > 0 ? this.websitePages : pagesToUse;

    if (pages && pages.length > 0) {
      this.logger.debug('Loading pages, count:', pages.length);

      pages.forEach((page: any) => {
        // Use the same ID generation logic as getPageId() - prefer UUID id from API
        const pageId = this.getPageId(page);

        // Access page data from jsonData.page or json_data.page (handle both formats)
        // Parent component uses jsonData (camelCase), raw API uses json_data (snake_case)
        const pageData = page.jsonData?.page || page.json_data?.page || {};

        // Extract title and description from API data
        const pageTitle = pageData.title || page.name || '';
        const pageDescription = pageData.description || '';

        // Always update SEO settings from API data (API is source of truth)
        // This ensures we always have the latest data from the API
        this.seoSettings[pageId] = {
          pageTitle: pageTitle,
          pageDescription: pageDescription,
        };

        this.logger.debug(`âœ“ Loaded Page: "${page.name}"`);
        this.logger.debug(`  - ID: ${pageId}`);
        this.logger.debug(`  - Title: "${pageTitle}"`);
        this.logger.debug(`  - Description: "${pageDescription}"`);
        this.logger.debug(`  - jsonData exists: ${Boolean(page.jsonData)}`);
        this.logger.debug(`  - json_data exists: ${Boolean(page.json_data)}`);
        this.logger.debug(`  - jsonData.page exists: ${Boolean(page.jsonData?.page)}`);
        this.logger.debug(`  - json_data.page exists: ${Boolean(page.json_data?.page)}`);
        this.logger.debug(`  - Full pageData:`, pageData);
      });

      this.logger.debug('All SEO Settings:', this.seoSettings);
    } else {
      this.logger.warn('No pages found to load.', { websitePages: this.websitePages, pagesToUse });
    }
  }

  onTabChange(tabId: string): void {
    this.activeTab = tabId as 'site-settings' | 'site-images' | 'analytics';
  }

  onSidebarItemClick(item: 'general' | 'domains'): void {
    this.activeSidebarItem = item;
    this.activeSeoItem = null; // Clear SEO selection when Settings item is clicked
  }

  /**
   * Handle clicking an SEO item: set active SEO selection, clear settings selection, load/refresh SEO settings for the page from available page data, and log results.
   * @example
   * nSeoItemClick('page-123')
   * undefined
   * @param {{string}} {{pageId}} - ID of the page that was clicked.
   * @returns {{void}} No return value; updates component state (activeSeoItem, activeSidebarItem, seoSettings) and logs or warns.
   **/
  onSeoItemClick(pageId: string): void {
    this.activeSeoItem = pageId;
    this.activeSidebarItem = null; // Clear Settings selection when SEO item is clicked

    // Always ensure SEO settings are loaded for this page (reload from API to ensure latest data)
    const page = this.websitePages.find((p: any) => this.getPageId(p) === pageId);
    if (page) {
      // Handle both jsonData (from parent component) and json_data (from raw API)
      const pageData = page.jsonData?.page || page.json_data?.page || {};
      const pageTitle = pageData.title || page.name || '';
      const pageDescription = pageData.description || '';

      // Always update from API data (source of truth)
      this.seoSettings[pageId] = {
        pageTitle: pageTitle,
        pageDescription: pageDescription,
      };

      this.logger.debug(`Clicked page: ${page.name} (ID: ${pageId})`);
      this.logger.debug(`Loaded settings:`, this.seoSettings[pageId]);
      this.logger.debug(`Page data structure:`, {
        hasJsonData: Boolean(page.jsonData),
        hasJson_data: Boolean(page.json_data),
        hasPage: Boolean(page.jsonData?.page || page.json_data?.page),
        pageData: pageData,
      });
    } else {
      this.logger.warn(`Page not found for ID: ${pageId}`);
      this.logger.debug(
        'Available pages:',
        this.websitePages.map(p => ({ name: p.name, id: this.getPageId(p) }))
      );
    }
  }

  /**
   * Get current SEO settings for the active page or the first website page, loading defaults from page data if needed.
   * @example
   * etCurrentSeoSettings()
   * { pageTitle: 'Home', pageDescription: 'Welcome to our site' }
   * @param {void} none - No arguments.
   * @returns {{pageTitle: string, pageDescription: string}} Return the SEO settings object with pageTitle and pageDescription.
   **/
  getCurrentSeoSettings() {
    let pageId: string | null = null;
    let page: any = null;

    if (!this.activeSeoItem) {
      // Return first page's settings if no page is selected
      if (this.websitePages.length > 0) {
        page = this.websitePages[0];
        pageId = this.getPageId(page);
      }
    } else {
      pageId = this.activeSeoItem;
      // Find the page by ID
      page = this.websitePages.find((p: any) => this.getPageId(p) === pageId);
    }

    // If we have a page, ensure settings are loaded
    if (page && pageId) {
      // Check if settings exist
      if (!this.seoSettings[pageId]) {
        // Load from page data - handle both jsonData (from parent) and json_data (from raw API)
        const pageData = page.jsonData?.page || page.json_data?.page || {};
        this.seoSettings[pageId] = {
          pageTitle: pageData.title || page.name || '',
          pageDescription: pageData.description || '',
        };
        this.logger.debug(`Loaded settings for page ${pageId}:`, this.seoSettings[pageId]);
      }

      // Return the settings
      return this.seoSettings[pageId] || { pageTitle: '', pageDescription: '' };
    }

    // Fallback: return existing settings if pageId exists
    if (pageId && this.seoSettings[pageId]) {
      return this.seoSettings[pageId];
    }

    return { pageTitle: '', pageDescription: '' };
  }

  /**
   * Get page name for display
   */
  getPageName(page: any): string {
    return page.name || 'Untitled Page';
  }

  /**
   * Get page ID for tracking
   * Use the UUID id from API if available, otherwise generate from name
   */
  getPageId(page: any): string {
    // Parent component stores original id in pageId field, raw API uses id field
    if (page.pageId) {
      return page.pageId;
    }
    // Use the UUID id from API (e.g., "7c14730f-883c-40e3-ad35-bfd4ece446a3")
    if (page.id) {
      return page.id;
    }
    // Fallback to generated ID from name
    return page.name?.toLowerCase().replace(/\s+/g, '-') || '';
  }

  /**
   * Handle title change with debounced save
   */
  onSeoTitleChange(newTitle: string): void {
    if (!this.activeSeoItem) {
      // If no page selected, try to use first page
      if (this.websitePages.length > 0) {
        const firstPage = this.websitePages[0];
        this.activeSeoItem = this.getPageId(firstPage);
      } else {
        return;
      }
    }

    const currentSettings = this.getCurrentSeoSettings();
    this.updateSeoSettings(this.activeSeoItem, newTitle, currentSettings.pageDescription);
  }

  /**
   * Handle description change with debounced save
   */
  onSeoDescriptionChange(newDescription: string): void {
    if (!this.activeSeoItem) {
      // If no page selected, try to use first page
      if (this.websitePages.length > 0) {
        const firstPage = this.websitePages[0];
        this.activeSeoItem = this.getPageId(firstPage);
      } else {
        return;
      }
    }

    const currentSettings = this.getCurrentSeoSettings();
    this.updateSeoSettings(this.activeSeoItem, currentSettings.pageTitle, newDescription);
  }

  /**
   * Update SEO settings for current page
   */
  private updateSeoSettings(pageId: string, pageTitle: string, pageDescription: string): void {
    if (!pageId) {
      this.logger.warn('Cannot update SEO settings: no page ID available');
      return;
    }

    if (!this.seoSettings[pageId]) {
      this.seoSettings[pageId] = { pageTitle: '', pageDescription: '' };
    }
    this.seoSettings[pageId].pageTitle = pageTitle;
    this.seoSettings[pageId].pageDescription = pageDescription;

    // Trigger debounced save
    if (pageId && this.websiteId) {
      this.seoSaveSubject.next({ pageId, pageTitle, pageDescription });
    }
  }

  /**
   * Save page SEO settings to API
   */
  private savePageSeoSettings(pageId: string, pageTitle: string, pageDescription: string): void {
    if (!this.websiteId || !pageId) {
      this.logger.warn('Cannot save SEO settings: missing websiteId or pageId');
      return;
    }

    // Find the page in websitePages
    const page = this.websitePages.find((p: any) => this.getPageId(p) === pageId);
    if (!page) {
      this.logger.warn(`Page not found for ID: ${pageId}`);
      return;
    }

    // Get the current json_data structure (handle both jsonData and json_data)
    const currentJsonData = page.jsonData || page.json_data || {};

    // Ensure page object exists in json_data
    if (!currentJsonData.page) {
      currentJsonData.page = {};
    }

    // Update only title and description, preserve all other data
    const updatedJsonData = {
      ...currentJsonData,
      page: {
        ...currentJsonData.page,
        title: pageTitle,
        description: pageDescription,
      },
    };

    // Get the website page ID - use the original id from API (not the transformed pageId)
    // The page object from parent has pageId which is the UUID from API
    const websitePageId = page.pageId || page.id;

    if (!websitePageId) {
      this.logger.error('Cannot save: website page ID not found', page);
      this.messageService.add({
        severity: 'error',
        summary: 'Save Failed',
        detail: 'Page ID not found. Please refresh and try again.',
      });
      return;
    }

    // Get html_data if it exists (preserve it)
    const htmlData = page.htmlData || page.html_data || '';

    this.isSavingSeo = true;

    this.logger.debug('Saving SEO settings:', {
      websitePageId,
      pageTitle,
      pageDescription,
      websiteId: this.websiteId,
    });

    this.templateService
      .updateWebsitePage(websitePageId, updatedJsonData, htmlData, this.websiteId)
      .pipe(
        finalize(() => {
          this.isSavingSeo = false;
        }),
        take(1)
      )
      .subscribe({
        next: response => {
          this.logger.debug('SEO settings saved successfully:', response);

          // Update local page data to reflect saved changes
          if (page.jsonData) {
            page.jsonData = updatedJsonData;
          } else if (page.json_data) {
            page.json_data = updatedJsonData;
          }

          // Show success message (optional, can be removed if too noisy)
          // this.messageService.add({
          //   severity: 'success',
          //   summary: 'Saved',
          //   detail: 'Page SEO settings updated successfully',
          //   life: 2000
          // });
        },
        error: (error: HttpErrorResponse | Error) => {
          this.logger.error('Error saving SEO settings:', error);

          let errorMessage = 'Failed to save SEO settings. Please try again.';
          if (error instanceof HttpErrorResponse) {
            errorMessage = error.error?.message || errorMessage;
          }

          this.messageService.add({
            severity: 'error',
            summary: 'Save Failed',
            detail: errorMessage,
          });
        },
      });
  }

  onSave(): void {
    this.save.emit(this.settings);
  }

  onUnpublish(): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to unpublish your website from all domains?',
      header: 'Unpublish Website',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.unpublish.emit();
      },
    });
  }

  onClose(): void {
    this.close.emit();
  }

  isSaving(type: DomainType): boolean {
    return this.isDomainSaving && this.domainSavingType === type;
  }

  isDomainInputReady(type: DomainType): boolean {
    if (type === 'subdomain') {
      return Boolean(this.subdomainName?.trim()) && !this.subdomainError;
    }
    return Boolean(this.customDomainName?.trim()) && !this.customDomainError;
  }

  /**
   * Validates a subdomain and returns an error message for the first failing rule or an empty string if valid.
   * @example
   * alidateSubdomain('my-subdomain')
   * ''
   * @param {{string}} {{subdomain}} - Subdomain to validate.
   * @returns {{string}} Error message if invalid or an empty string when the subdomain is valid.
   **/
  validateSubdomain(subdomain: string): string {
    // Check if input contains spaces
    if (subdomain.includes(' ')) {
      return 'Subdomain cannot contain spaces';
    }

    const trimmed = subdomain.trim();

    if (!trimmed) {
      return 'Subdomain is required';
    }

    // Check if original input had trailing/leading spaces (user typed spaces)
    if (subdomain !== trimmed) {
      return 'Subdomain cannot contain spaces';
    }

    // Check length (3-63 characters for subdomain part)
    if (trimmed.length < 3) {
      return 'Subdomain must be at least 3 characters long';
    }

    if (trimmed.length > 63) {
      return 'Subdomain must be no more than 63 characters long';
    }

    // Only lowercase letters, numbers, and hyphens allowed
    const subdomainRegex = /^[a-z0-9-]+$/;
    if (!subdomainRegex.test(trimmed)) {
      return 'Subdomain can only contain lowercase letters, numbers, and hyphens';
    }

    // Cannot start or end with a hyphen
    if (trimmed.startsWith('-') || trimmed.endsWith('-')) {
      return 'Subdomain cannot start or end with a hyphen';
    }

    // Cannot have consecutive hyphens
    if (trimmed.includes('--')) {
      return 'Subdomain cannot contain consecutive hyphens';
    }

    return '';
  }

  /**
   * Validate a custom domain string and return an empty string if valid or an error message if invalid.
   * @example
   * alidateCustomDomain('https://www.example.com/path?query#frag')
   * ''
   * @param {{string}} {{domain}} - Domain string to validate (may include protocol, www, path, query, or fragment).
   * @returns {{string}} Return an empty string when the domain is valid; otherwise return a human-readable error message.
   **/
  validateCustomDomain(domain: string): string {
    // Check if input contains spaces (after removing protocol and www)
    const tempDomain = domain.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
    if (tempDomain.includes(' ')) {
      return 'Domain cannot contain spaces';
    }

    const trimmed = domain.trim();

    if (!trimmed) {
      return 'Domain is required';
    }

    // Check if original input had trailing/leading spaces (user typed spaces)
    if (domain !== trimmed) {
      return 'Domain cannot contain spaces';
    }

    // Remove protocol if present
    let domainToValidate = trimmed.replace(/^https?:\/\//i, '').replace(/^www\./i, '');

    // Remove trailing slash and path
    domainToValidate = domainToValidate.split('/')[0].split('?')[0].split('#')[0];

    // Check if domain contains at least one dot
    if (!domainToValidate.includes('.')) {
      return 'Domain must include a top-level domain (e.g., .com, .org)';
    }

    // Check length (1-253 characters for full domain)
    if (domainToValidate.length < 4) {
      return 'Domain is too short';
    }

    if (domainToValidate.length > 253) {
      return 'Domain is too long (maximum 253 characters)';
    }

    // Valid domain format: letters, numbers, dots, and hyphens
    // Each label (part between dots) must be 1-63 characters
    const domainRegex = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
    if (!domainRegex.test(domainToValidate)) {
      return 'Please enter a valid domain name (e.g., example.com)';
    }

    // Check each label (part between dots)
    const labels = domainToValidate.split('.');
    for (const label of labels) {
      if (label.length < 1 || label.length > 63) {
        return 'Each part of the domain must be 1-63 characters long';
      }

      if (label.startsWith('-') || label.endsWith('-')) {
        return 'Domain parts cannot start or end with a hyphen';
      }

      // Only letters, numbers, and hyphens in each label
      const labelRegex = /^[a-z0-9-]+$/i;
      if (!labelRegex.test(label)) {
        return 'Domain can only contain letters, numbers, dots, and hyphens';
      }
    }

    // TLD must be at least 2 characters
    const tld = labels[labels.length - 1];
    if (tld.length < 2) {
      return 'Top-level domain must be at least 2 characters';
    }

    // TLD should only contain letters
    const tldRegex = /^[a-z]+$/i;
    if (!tldRegex.test(tld)) {
      return 'Top-level domain can only contain letters';
    }

    return '';
  }

  onSubdomainInput(): void {
    // Remove spaces in real-time
    if (this.subdomainName.includes(' ')) {
      this.subdomainName = this.subdomainName.replace(/\s/g, '');
    }
    this.subdomainError = this.validateSubdomain(this.subdomainName);
  }

  /**
   * Remove spaces from the custom domain input in real-time while preserving protocol (http/https) and overall URL structure.
   * @example
   * this.nCustomDomainInput()
   * // void (updates this.customDomainName and this.customDomainError)
   * @returns {void} Updates component state (this.customDomainName and this.customDomainError) without returning a value.
   */
  onCustomDomainInput(): void {
    // Remove spaces in real-time (but preserve structure for protocol/www removal)
    const original = this.customDomainName;
    // Only remove spaces from the actual domain part (after protocol/www)
    let cleaned = original.replace(/^https?:\/\//i, match => {
      const protocol = match;
      const rest = original.substring(match.length);
      return protocol + rest.replace(/\s/g, '');
    });
    if (!cleaned.includes('://')) {
      cleaned = cleaned.replace(/\s/g, '');
    } else {
      const parts = cleaned.split('://');
      parts[1] = parts[1].replace(/\s/g, '');
      cleaned = parts.join('://');
    }

    if (cleaned !== original) {
      this.customDomainName = cleaned;
    }
    this.customDomainError = this.validateCustomDomain(this.customDomainName);
  }

  /**
   * Validate the chosen domain (subdomain or custom), initiate an update to the website domain, manage saving state, and show success/error messages.
   * @example
   * onDomainConfirm('subdomain')
   * Promise<void>
   * @param {{DomainType}} {{type}} - The domain type to confirm, e.g. 'subdomain' or 'custom'.
   * @returns {{Promise<void>}} Returns a Promise that resolves when the domain confirmation process has been initiated/completed (no value).
   **/
  async onDomainConfirm(type: DomainType): Promise<void> {
    if (this.isDomainSaving) {
      return;
    }

    // Validate before proceeding
    let validationError = '';
    if (type === 'subdomain') {
      validationError = this.validateSubdomain(this.subdomainName);
      this.subdomainError = validationError;
    } else {
      validationError = this.validateCustomDomain(this.customDomainName);
      this.customDomainError = validationError;
    }

    if (validationError) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid domain',
        detail: validationError,
      });
      return;
    }

    if (!this.isDomainInputReady(type)) {
      return;
    }

    const websiteId = this.getWebsiteId();
    if (!websiteId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Missing website',
        detail: 'Website ID is required to update the domain.',
      });
      return;
    }

    const domainName = this.buildDomainName(type);
    if (!domainName) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid domain',
        detail: 'Please provide a valid domain name.',
      });
      return;
    }

    this.isDomainSaving = true;
    this.domainSavingType = type;

    const payload = {
      domain_type: type,
      domain_name: domainName,
      website_id: websiteId,
    };

    this.templateService
      .updateWebsiteDomain(payload)
      .pipe(
        take(1),
        finalize(() => {
          this.isDomainSaving = false;
          this.domainSavingType = null;
        })
      )
      .subscribe({
        next: response => {
          this.messageService.add({
            severity: 'success',
            summary: 'Domain updated',
            detail:
              (typeof response === 'object' && response?.message) ||
              'Domain details updated successfully.',
          });
          this.domainConfirmed.emit();
        },
        error: (error: HttpErrorResponse | Error) => {
          let detail = 'Failed to update domain.';

          if (error instanceof HttpErrorResponse) {
            detail =
              (typeof error.error === 'object' && error.error?.message) ||
              error.error ||
              error.message ||
              detail;
          } else if (error instanceof Error) {
            detail = error.message;
          }

          this.messageService.add({
            severity: 'error',
            summary: 'Domain update failed',
            detail,
          });
        },
        complete: () => {},
      });
  }

  private getWebsiteId(): string | null {
    return (
      this.websiteId ||
      this.websiteData?.id ||
      this.websiteData?.websiteId ||
      this.websiteData?.website_id ||
      null
    );
  }

  /**
   * Normalize and return a domain name for either a subdomain or a custom domain, updating the corresponding component property.
   * @example
   * buildDomainName('subdomain')
   * 'blog' // or null if empty
   * @param {{DomainType}} {{type}} - 'subdomain' to process this.subdomainName; otherwise processes this.customDomainName.
   * @returns {{string|null}} Normalized, lowercase domain without protocol, www, or path, or null if the result is empty.
   **/
  private buildDomainName(type: DomainType): string | null {
    if (type === 'subdomain') {
      const trimmed = this.subdomainName.trim().toLowerCase();
      this.subdomainName = trimmed;
      return trimmed || null;
    }

    // Clean up custom domain: remove protocol, www, trailing slashes, paths
    let cleaned = this.customDomainName.trim().toLowerCase();

    // Remove protocol
    cleaned = cleaned.replace(/^https?:\/\//i, '');

    // Remove www. prefix
    cleaned = cleaned.replace(/^www\./i, '');

    // Remove trailing slash and everything after it (path, query, fragment)
    cleaned = cleaned.split('/')[0].split('?')[0].split('#')[0];

    this.customDomainName = cleaned;
    return cleaned || null;
  }

  /**
   * Prompt the user to select an image file and replace the component's favicon preview with the selected image.
   * @example
   * nReplaceFavicon()
   * undefined
   * @returns {void} Triggers a file picker and updates this.faviconPreview with a data URL of the chosen image (no return value).
   */
  onReplaceFavicon(): void {
    // TODO: Implement favicon upload functionality
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          this.faviconPreview = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  }

  /**
   * Opens a file picker to select an image file and assigns the selected image's data URL to this.socialPreview.
   * @example
   * nUploadSocialPreview()
   * undefined
   * @param {{void}} {{none}} - No arguments are required.
   * @returns {{void}} Triggers a file selection and updates component state; does not return a value.
   **/
  onUploadSocialPreview(): void {
    // TODO: Implement social preview upload functionality
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          this.socialPreview = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  }

  onAnalyticsInput(): void {
    // Track analytics input changes if needed
  }

  onSaveAnalytics(): void {
    // TODO: Implement API call to save analytics settings
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Analytics settings saved successfully',
    });
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
